/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Link } from 'react-router-dom';
import { SEO, serviceIcons, ServiceKey, ArrowRightIcon } from '../components/common';
import { colors, borderRadius, shadows, typography, transitions } from '../styles/theme';

const pageStyles = css`
  background: ${colors.background.secondary};
  min-height: 100vh;
`;

const heroStyles = css`
  background: linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[800]} 100%);
  color: white;
  padding: 4rem 1rem;
  text-align: center;

  @media (min-width: 768px) {
    padding: 5rem 2rem;
  }
`;

const heroContentStyles = css`
  max-width: 800px;
  margin: 0 auto;

  h1 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['3xl']};
    font-weight: ${typography.fontWeight.extrabold};
    margin-bottom: 1rem;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['4xl']};
    }
  }

  p {
    font-size: ${typography.fontSize.lg};
    color: ${colors.primary[100]};
    margin: 0;
  }
`;

const sectionStyles = css`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;

  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const sectionHeaderStyles = css`
  text-align: center;
  margin-bottom: 3rem;

  h2 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['2xl']};
    color: ${colors.text.primary};
    margin-bottom: 0.75rem;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['3xl']};
    }
  }

  p {
    color: ${colors.text.secondary};
    max-width: 600px;
    margin: 0 auto;
  }
`;

const cardsGridStyles = css`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const serviceCardStyles = css`
  background: white;
  border-radius: ${borderRadius['2xl']};
  padding: 2.5rem 2rem;
  text-align: center;
  box-shadow: ${shadows.lg};
  transition: all ${transitions.normal};
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${shadows['2xl']};
    border-color: ${colors.primary[200]};
  }

  .icon-wrapper {
    width: 100px;
    height: 100px;
    margin: 0 auto 1.5rem;
    border-radius: ${borderRadius.full};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 44px;
      height: 44px;
    }
  }

  h3 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize.xl};
    font-weight: ${typography.fontWeight.bold};
    color: ${colors.text.primary};
    margin-bottom: 0.75rem;
  }

  p {
    color: ${colors.text.secondary};
    font-size: ${typography.fontSize.sm};
    line-height: 1.7;
    margin-bottom: 1.5rem;
  }

  .cta {
    margin-top: auto;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: ${colors.primary[700]};
    font-weight: ${typography.fontWeight.semibold};
    font-size: ${typography.fontSize.sm};

    svg {
      width: 16px;
      height: 16px;
      transition: transform ${transitions.fast};
    }
  }

  &:hover .cta svg {
    transform: translateX(3px);
  }
`;

const driverCardStyles = css`
  ${serviceCardStyles}
  background: linear-gradient(135deg, ${colors.secondary[500]} 0%, ${colors.secondary[700]} 100%);
  color: white;

  &:hover {
    color: white;
  }

  h3 {
    color: white;
  }

  p {
    color: rgba(255, 255, 255, 0.9);
  }

  .cta {
    color: white;
    background: rgba(255, 255, 255, 0.2);
    padding: 0.5rem 1rem;
    border-radius: ${borderRadius.lg};
  }

  &:hover {
    border-color: ${colors.secondary[300]};
  }
`;

const serviceOptions: {
  id: ServiceKey;
  title: string;
  description: string;
  color: string;
  iconColor: string;
  path: string;
}[] = [
  {
    id: 'cekici',
    title: 'Çekici Hizmeti',
    description: 'Aracınız yolda mı kaldı? Çekici talebi nasıl oluşturulur adım adım öğrenin.',
    color: colors.primary[100],
    iconColor: colors.primary[600],
    path: '/nasil-kullanilir/cekici',
  },
  {
    id: 'vinc',
    title: 'Vinç Hizmeti',
    description: 'Ağır vasıta veya özel durumlar için vinç talebi oluşturma rehberi.',
    color: colors.secondary[100],
    iconColor: colors.secondary[600],
    path: '/nasil-kullanilir/vinc',
  },
  {
    id: 'nakliye',
    title: 'Nakliye Hizmeti',
    description: 'Ev, ofis veya ticari nakliye talebi nasıl oluşturulur öğrenin.',
    color: '#fef3c7',
    iconColor: '#d97706',
    path: '/nasil-kullanilir/nakliye',
  },
  {
    id: 'yol-yardim',
    title: 'Yol Yardım',
    description: 'Lastik, akü, yakıt gibi acil yol yardım talebi oluşturma adımları.',
    color: '#dcfce7',
    iconColor: '#16a34a',
    path: '/nasil-kullanilir/yol-yardim',
  },
  {
    id: 'servis',
    title: 'Servis',
    description: 'Personel, öğrenci ve organizasyon taşımacılığı için servis talebi oluşturma rehberi.',
    color: colors.accent[100],
    iconColor: colors.accent[600],
    path: '/nasil-kullanilir/servis',
  },
  {
    id: 'vip-tasima',
    title: 'VIP Taşıma',
    description: 'Mercedes Vito gibi VIP araçlarla havaalanı transferi ve yolcu taşıma talebi rehberi.',
    color: colors.premium[100],
    iconColor: colors.premium[700],
    path: '/nasil-kullanilir/vip-tasima',
  },
];

const DriverIcon = serviceIcons.surucu;

export default function HowToUsePage() {
  return (
    <div css={pageStyles}>
      <SEO
        title="Nasıl Kullanılır?"
        description="YolSepetiGO uygulamasını nasıl kullanacağınızı adım adım öğrenin. Çekici, vinç, nakliye ve yol yardım talepleri için rehberler."
        keywords={['nasıl kullanılır', 'çekici talebi', 'vinç talebi', 'nakliye talebi', 'yol yardım']}
      />

      {/* Hero */}
      <section css={heroStyles}>
        <div css={heroContentStyles}>
          <h1>Nasıl Kullanılır?</h1>
          <p>Hangi hizmeti almak istiyorsunuz? Size özel rehberi seçin ve adım adım ilerleyin.</p>
        </div>
      </section>

      {/* Müşteri Hizmetleri */}
      <section css={sectionStyles}>
        <div css={sectionHeaderStyles}>
          <h2>Müşteri Olarak</h2>
          <p>Hizmet almak için aşağıdaki seçeneklerden birini seçin</p>
        </div>

        <div css={cardsGridStyles}>
          {serviceOptions.map((service) => {
            const Icon = serviceIcons[service.id];
            return (
              <Link key={service.id} to={service.path} css={serviceCardStyles}>
                <div
                  className="icon-wrapper"
                  style={{ background: service.color, color: service.iconColor }}
                >
                  <Icon />
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <span className="cta">
                  Rehberi İncele <ArrowRightIcon />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Sürücü Bölümü */}
      <section css={sectionStyles}>
        <div css={sectionHeaderStyles}>
          <h2>Sürücü Olarak</h2>
          <p>YolSepetiGO ailesine katılmak mı istiyorsunuz?</p>
        </div>

        <div css={css`max-width: 500px; margin: 0 auto;`}>
          <Link to="/nasil-kullanilir/surucu" css={driverCardStyles}>
            <div className="icon-wrapper" style={{ background: 'rgba(255,255,255,0.2)' }}>
              <DriverIcon />
            </div>
            <h3>Sürücü Uygulaması</h3>
            <p>Çekici, vinç, nakliye veya yol yardım sürücüsü olarak nasıl iş alırsınız? Adım adım rehber.</p>
            <span className="cta">
              Sürücü Rehberini İncele <ArrowRightIcon />
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
